import React from 'react';
import PublicLayout from '../components/PublicLayout';
import { useTranslation } from 'react-i18next';
import { AboutSEO } from '../components/SEO';
import { FloatingShapes, MultiLanguageIcon, CustomDesignIcon } from '../components/icons/FeatureIcons';
import { Shield, Users } from 'lucide-react';

// Custom icon wrappers for Why section
const SecurityIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-200">
    <Shield className="w-6 h-6 text-white" />
  </div>
);

const SupportIcon = () => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200">
    <Users className="w-6 h-6 text-white" />
  </div>
);

const About = () => {
  const { t } = useTranslation();
  
  const whyFeatures = [
    { Icon: MultiLanguageIcon, text: t('pages.about.whyFeature1') },
    { Icon: CustomDesignIcon, text: t('pages.about.whyFeature2') },
    { Icon: SecurityIcon, text: t('pages.about.whyFeature3') },
    { Icon: SupportIcon, text: t('pages.about.whyFeature4') },
  ];

  return (
    <PublicLayout>
      <AboutSEO />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 py-20 overflow-hidden">
        <FloatingShapes />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <p className="text-sm font-semibold text-purple-200 uppercase tracking-wide mb-4">{t('footer.about')}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">{t('pages.about.title')}</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">{t('pages.about.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {/* Mission & Vision Cards */}
        <div className="grid md:grid-cols-2 gap-8 -mt-24 relative z-20">
          <div className="p-8 rounded-2xl bg-white shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('pages.about.mission')}</h3>
            <p className="text-gray-600 leading-relaxed">{t('pages.about.missionDesc')}</p>
          </div>
          <div className="p-8 rounded-2xl bg-white shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('pages.about.vision')}</h3>
            <p className="text-gray-600 leading-relaxed">{t('pages.about.visionDesc')}</p>
          </div>
        </div>

        {/* Why idemenu Section */}
        <div className="text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-10">{t('pages.about.whyTitle')}</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {whyFeatures.map((feature, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-5 p-6 bg-white rounded-2xl text-left shadow-md border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all group"
              >
                <div className="flex-shrink-0">
                  <feature.Icon />
                </div>
                <span className="text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default About;
